/**
 * Single source of truth for DataTable density.
 *
 * Density changes spacing, not type size. Names stay 16px and amounts stay
 * prominent in every preset (SPR-1140 / HAT-1201 / HAT-1205). The phone date
 * rail is 15px Gregorian over a 14px Hebrew line; HAT-1217 hugs that rail
 * with max-content instead of a reserved 96px track. DataTable feeds `rowH`
 * to the virtualizer and writes the rest as CSS custom properties, so the
 * stylesheet and the scroll math cannot drift apart.
 *
 * `airy` is offered on desktop only (HAT-1189). Mobile keeps the two-option
 * control (`MOBILE_TABLE_DENSITIES`) because the phone layout is already
 * roomy enough and airy row heights would waste the short viewport.
 */
export const TABLE_DENSITIES = ['airy', 'normal', 'compact'] as const;
export const MOBILE_TABLE_DENSITIES = ['normal', 'compact'] as const;

export type TableDensity = (typeof TABLE_DENSITIES)[number];

export type TableDensityTokens = {
  rowH: number;
  cellPy: number;
  cellPx: number;
  cellFs: number;
  cellLh: number;
  headFs: number;
  headLh: number;
  headH: number;
};

/**
 * The layout every board had before density existed. It stays the DataTable default so
 * boards that opt out keep their current look. Existing element-scrolling mobile
 * boards retain it; document boards use the mobile density heights below.
 */
export const LEGACY_TABLE_DENSITY: TableDensity = 'airy';

/**
 * Desktop scale (SPR-1140). Airy / normal keep 16px body type; compact keeps the same 16px text.
 * Row pitch matches the old site (~38px) at compact so a 800px viewport shows
 * ~22 rows instead of ~15.
 */
export const TABLE_DENSITY_TOKENS: Record<TableDensity, TableDensityTokens> = {
  airy: {
    rowH: 56,
    cellPy: 8,
    cellPx: 12,
    cellFs: 16,
    cellLh: 24,
    headFs: 16,
    headLh: 20,
    headH: 44,
  },
  normal: {
    rowH: 44,
    cellPy: 4,
    cellPx: 12,
    cellFs: 16,
    cellLh: 24,
    headFs: 16,
    headLh: 20,
    headH: 40,
  },
  compact: {
    rowH: 36,
    cellPy: 0,
    cellPx: 8,
    cellFs: 16,
    cellLh: 22,
    headFs: 16,
    headLh: 22,
    headH: 34,
  },
};

export const TABLE_DENSITY_ROW_HEIGHT_PX: Record<TableDensity, number> = {
  airy: TABLE_DENSITY_TOKENS.airy.rowH,
  normal: TABLE_DENSITY_TOKENS.normal.rowH,
  compact: TABLE_DENSITY_TOKENS.compact.rowH,
};

/**
 * Legacy element-scrolling mobile tables retain their existing two-line layout.
 *
 * This is what those two lines actually measure - a 24px prefix line above a 28px
 * name line, both held to one line each by `white-space: nowrap`. It used to say 52,
 * which left every row overlapping the next by five pixels: a tap along the bottom
 * edge of a row opened the transaction below it.
 */
export const MOBILE_ROW_HEIGHT_PX = 57;

/**
 * Row height for a board whose cells carry a second line on the desktop presets.
 *
 * The presets above describe a one-line row, and the mobile card was the only two-line
 * layout they had to account for. A board that folds a column into a sub-line - which is
 * how the standing-orders board fits its numbers without truncating them - needs the room
 * for it, or the second line spills into the row beneath.
 *
 * The numbers are the one-line pitch plus a 16px sub-line and the 2px that separates it.
 * Compact is unchanged, because compact drops second lines rather than making room: that is
 * what the preset is for.
 */
export const TABLE_DENSITY_TWO_LINE_ROW_H: Record<TableDensity, number> = {
  airy: 74,
  normal: 62,
  compact: TABLE_DENSITY_TOKENS.compact.rowH,
};

// One-line mobile transactions use the same compact pitch as desktop.
export const MOBILE_DENSITY_ROW_HEIGHT_PX: Record<TableDensity, number> = { normal: 44, compact: 36, airy: 44 };

/**
 * Phone transactions board (HAT-1217 / HAT-1205).
 *
 * Tracks are `max-content | minmax(0, 1fr) | max-content` so date and amount
 * hug their text and the name takes whatever remains. Zebra (#eeeeee / white)
 * is painted on the full row, not the three content cells. Cover widths are
 * shared with HAT-1218; phone rules apply only below 768. Table chrome keeps
 * `TABLE_RADIUS_PX` (8px) — HAT-1207; do not zero it for a viewport bleed.
 *
 * Longest-Hebrew / typical-amount figures stay as crush-check ceilings — they
 * are not CSS track sizes. Variant B is two stacked lines (primary + secondary)
 * so the virtualizer pitch cannot drift from --row-pad.
 */
export const PHONE_BOARD_COVER_WIDTHS_PX = [280, 320, 340, 360, 390] as const;
export const PHONE_BOARD_DESKTOP_MIN_PX = 768;
export const PHONE_TRANSACTION_GRID_COLUMNS = 'max-content minmax(0, 1fr) max-content';
export const PHONE_TRANSACTION_ZEBRA_ALT = '#eeeeee';
export const PHONE_TRANSACTION_ZEBRA_BASE = '#ffffff';
/** Longest Hebrew month (כ״ט במרחשוון ≈ 96px at 14px) — crush-check only. */
export const PHONE_TRANSACTION_DATE_HUG_MAX_PX = 96;
/** Typical 18px/800 ILS amount ceiling — crush-check only, not a reserved track. */
export const PHONE_TRANSACTION_AMOUNT_HUG_MAX_PX = 108;
export const PHONE_TRANSACTION_GAP_PX = 8;
export const PHONE_TRANSACTION_ROW_INLINE_PAD_PX = 8;
/** Shared table chrome radius (HAT-1207). Phone must not zero this for the bleed. */
export const TABLE_RADIUS_PX = 8;
export const PHONE_TRANSACTION_ROW_PAD_PX: Record<TableDensity, number> = { normal: 10, compact: 6, airy: 10 };
/** Primary 15/17 + secondary Hebrew 12/16 (HAT-1210 Variant B). */
const PHONE_DATE_RAIL_PX = 17 + 16;
const PHONE_NAME_LINE_PX = Math.ceil(16 * 1.35);
const PHONE_NAME_METHOD_PX = 16;
export const PHONE_TRANSACTION_NAME_GAP_PX = 4;
const phoneNameBlockPx = (lines: 1 | 2): number => PHONE_NAME_LINE_PX * lines + PHONE_TRANSACTION_NAME_GAP_PX + PHONE_NAME_METHOD_PX;

const phoneTransactionRowHeight = (density: TableDensity): number => {
  const nameLines = density === 'compact' ? 1 : 2;
  return Math.max(PHONE_DATE_RAIL_PX, phoneNameBlockPx(nameLines)) + PHONE_TRANSACTION_ROW_PAD_PX[density] * 2;
};

export const MOBILE_TRANSACTION_ROW_HEIGHT_PX: Record<TableDensity, number> = {
  normal: phoneTransactionRowHeight('normal'),
  compact: phoneTransactionRowHeight('compact'),
  airy: phoneTransactionRowHeight('airy'),
};

/** Remaining pixels for the name column after hugged date + amount + gaps. */
export const phoneBoardDetailWidthPx = (
  viewportPx: number,
  datePx = PHONE_TRANSACTION_DATE_HUG_MAX_PX,
  amountPx = PHONE_TRANSACTION_AMOUNT_HUG_MAX_PX
): number => viewportPx - datePx - amountPx - PHONE_TRANSACTION_GAP_PX * 2;

export const phoneTransactionBoardCssVars = (density: TableDensity): Record<string, string> => ({
  '--date-col': 'max-content',
  '--name-col': 'minmax(0, 1fr)',
  '--amount-col': 'max-content',
  '--grid-columns': PHONE_TRANSACTION_GRID_COLUMNS,
  '--grid-gap': `${PHONE_TRANSACTION_GAP_PX}px`,
  '--row-pad': `${PHONE_TRANSACTION_ROW_PAD_PX[density]}px`,
  '--row-inline-pad': `${PHONE_TRANSACTION_ROW_INLINE_PAD_PX}px`,
  '--name-gap': `${PHONE_TRANSACTION_NAME_GAP_PX}px`,
  '--zebra-alt': PHONE_TRANSACTION_ZEBRA_ALT,
  '--zebra-base': PHONE_TRANSACTION_ZEBRA_BASE,
  '--table-radius': `${TABLE_RADIUS_PX}px`,
});

export const TABLE_DENSITY_STORAGE_KEY = 'hz.tableDensity';

export const isTableDensity = (value: unknown): value is TableDensity => TABLE_DENSITIES.includes(value as TableDensity);

export const densityCssVars = (tokens: TableDensityTokens, rowHeightPx: number): Record<string, string> => ({
  '--row-h': `${rowHeightPx}px`,
  '--data-table-row-height': `${rowHeightPx}px`,
  '--cell-py': `${tokens.cellPy}px`,
  '--cell-px': `${tokens.cellPx}px`,
  '--cell-fs': `${tokens.cellFs}px`,
  '--cell-lh': `${tokens.cellLh}px`,
  '--head-fs': `${tokens.headFs}px`,
  '--head-lh': `${tokens.headLh}px`,
  '--head-h': `${tokens.headH}px`,
  '--table-radius': `${TABLE_RADIUS_PX}px`,
});
